/**
 * Shared per-run row formatters, extracted out of `RunsTab` to break the import
 * cycle `RunsTab → RunTimeline → RunsTab`: `RunTimeline` needs these formatters
 * and `RunsTab` renders `RunTimeline`.
 *
 * `RunsTab` re-exports every symbol here, so its existing importers (UsageTab,
 * RunsTab.test.tsx) keep working unchanged.
 */

import type { Run } from './queries';

/** Group-formatted token count, or an em-dash for null/undefined. */
export function fmtTokens(n?: number): string {
  return n === null || n === undefined ? '—' : n.toLocaleString();
}

/** Fixed 4-dp USD cost, e.g. `$0.1234`. */
export function fmtCost(n: number): string {
  return `$${n.toFixed(4)}`;
}

/** Why a turn shows no cost while the run above it does. */
export const TURN_COST_UNREPORTED =
  'Per-turn cost is producer-reported and this producer reported none. '
  + "The run's cost is derived from token counts, so it has no per-turn breakdown.";

/**
 * Whether a turn's cost figure is REAL or merely absent.
 *
 * The events route now DERIVES per-turn cost and states the answer directly with
 * an `unpriced` flag, so trust the field. It previously returned stored rows
 * verbatim, which forced this to infer "absent" from `turn === 0 && run > 0` — an
 * inference that is now wrong in a real case: a turn genuinely priced at $0 (no
 * billable tokens) inside a costed run would be hidden behind a placeholder.
 *
 * The inference survives ONLY as a fallback for a response predating the field,
 * which is possible for the few minutes a cached console talks to the older API.
 * Once that cannot happen, delete the second half.
 */
export function turnCostIsUnreported(
  turnCostUsd: number | undefined,
  runCostUsd: number,
  unpriced?: boolean,
): boolean {
  if (typeof unpriced === 'boolean') return unpriced;
  return runCostUsd > 0 && !(typeof turnCostUsd === 'number' && turnCostUsd > 0);
}

/**
 * The "N turns unpriced" badge, or null when everything resolved to a rate.
 *
 * An unpriceable model MUST be reported rather than hidden: its tokens
 * contribute nothing to `cost_usd`, so the figure beside it is under-counted and
 * a bare `$0.0000` would read as a real price. The offending models are named in
 * the title so the fix (add an override on the Prices tab) is one hover away.
 *
 * The prop is the unpriced FIELDS, not a whole `Run`, so the one badge serves
 * both a single run (RunsTab) and a summed window (UsageTab) — spec §7 requires
 * the indicator on "any run OR AGGREGATE". Do not fork a second badge.
 */
export function UnpricedBadge({ run }: { run: Pick<Run, 'unpriced_turns' | 'unpriced_models'> }) {
  const turns = run.unpriced_turns ?? 0;
  if (turns <= 0) return null;
  const models = run.unpriced_models ?? [];
  return (
    <span
      className="pill pill-warn unpriced-badge"
      title={models.length ? `No rate for: ${models.join(', ')}` : 'No rate for some models'}
    >
      {turns} {turns === 1 ? 'turn' : 'turns'} unpriced
    </span>
  );
}
